import { useEffect, useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';
import { useCombinedPagination } from '~/modules/resourcing/common/hooks/useCombinedPagination';
import { useMeContext } from '~/modules/me/useMeContext';
import { useResourcingChartToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import { getPageOfResourcePlanAllocatedUserTasks } from './getResourcePlanAllocatedUserTasks';

const keepHierarchySort = () => 0;

const shouldKeepTaskAssignment = ({
  taskAssignment,
  taskId,
  isResourceActualModeEnabled
}) => {
  if (taskAssignment.task.id !== taskId) return true;

  if (isResourceActualModeEnabled && taskAssignment.hasTimeEntered) {
    return true;
  }

  return false;
};

export const getMappedTaskAssignmentsData = combinedPaginationData => {
  return Object.values(
    (combinedPaginationData || []).reduce((acc, taskAssignment) => {
      acc[taskAssignment.task.id] =
        acc[taskAssignment.task.id] || taskAssignment;

      return acc;
    }, {})
  );
};

export const updateTaskAssignmentsListFocusableElementState = ({
  userTaskAssignmentsData,
  setTaskAssignmentRowNextFocus,
  removedTaskId,
  isResourceActualModeEnabled
}) => {
  const filteredTaskAssignments = userTaskAssignmentsData.filter(
    taskAssignment => !taskAssignment.isUnassigned
  );

  const removedItemIndex = filteredTaskAssignments.findIndex(
    taskAssignment =>
      !shouldKeepTaskAssignment({
        taskAssignment,
        taskId: removedTaskId,
        isResourceActualModeEnabled
      })
  );

  const nextFocusableTaskId =
    removedItemIndex > -1
      ? filteredTaskAssignments[removedItemIndex + 1]?.task.id ||
        filteredTaskAssignments[removedItemIndex - 1]?.task.id
      : undefined;

  setTaskAssignmentRowNextFocus?.(nextFocusableTaskId);
};

export const useFetchResourceUserTaskAssignments = ({
  userId,
  projectId,
  pageSize,
  currentPage,
  setCurrentPage,
  handleRemoveResourceAllocation,
  setTaskAssignmentRowNextFocus
}) => {
  const apolloClient = useApolloClient();
  const { permissionsMap } = useMeContext();
  const { isResourceActualModeEnabled } = useResourcingChartToolbarContext();

  const isCompleteResourceAllocationEditEnabled = Boolean(
    permissionsMap['urn:replicon-webui:completed-resource-allocation:edit']
  );

  const fetchResourcePlanAllocatedUserTasks = async page => {
    const {
      resourcePlanAllocatedUserTasks
    } = await getPageOfResourcePlanAllocatedUserTasks({
      page: page + 1,
      pageSize,
      userId,
      projectId,
      includeTimeEnteredTasks: isResourceActualModeEnabled,
      apolloClient
    });

    return resourcePlanAllocatedUserTasks;
  };

  const {
    loading,
    data,
    setData,
    getNext,
    refetch,
    reset
  } = useCombinedPagination({
    getters: [fetchResourcePlanAllocatedUserTasks],
    sort: keepHierarchySort
  });

  const userTaskAssignmentsData = useMemo(() => {
    return loading && currentPage === 1
      ? {}
      : getMappedTaskAssignmentsData(data);
  }, [loading, currentPage, data]);

  useEffect(() => {
    if (
      !loading &&
      Object.keys(userTaskAssignmentsData).length < pageSize * currentPage
    ) {
      getNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleRemoveTaskAssignment = useCallback(
    ({ taskId, isResourceAllocationDeleted }) => {
      const updatedData = userTaskAssignmentsData.reduce(
        (acc, taskAssignment) => {
          if (taskAssignment.task.id !== taskId) {
            acc.push(taskAssignment);
          } else if (
            isResourceActualModeEnabled &&
            taskAssignment.hasTimeEntered
          ) {
            acc.push({
              ...taskAssignment,
              isUnassigned: true
            });
          }

          return acc;
        },
        []
      );

      setData(updatedData);

      updateTaskAssignmentsListFocusableElementState({
        userTaskAssignmentsData,
        setTaskAssignmentRowNextFocus,
        removedTaskId: taskId,
        isResourceActualModeEnabled
      });

      if (
        isCompleteResourceAllocationEditEnabled &&
        isResourceAllocationDeleted
      ) {
        handleRemoveResourceAllocation(userId);
      }
    },
    [
      userTaskAssignmentsData,
      setData,
      setTaskAssignmentRowNextFocus,
      isCompleteResourceAllocationEditEnabled,
      isResourceActualModeEnabled,
      handleRemoveResourceAllocation,
      userId
    ]
  );

  const handleUpdateTaskAssignment = useCallback(() => {
    if (currentPage === 1) {
      refetch();
    } else {
      reset();
      setCurrentPage(1);
    }
  }, [currentPage, setCurrentPage, refetch, reset]);

  return {
    loading,
    userTaskAssignmentsData,
    handleRemoveTaskAssignment,
    handleUpdateTaskAssignment
  };
};

export default useFetchResourceUserTaskAssignments;
